<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';
  import { t } from '../i18nStore';

  let logs = [];
  let isLoading = true;
  let error = '';

  // Pagination
  let page = 1;
  const pageSize = 20;
  let totalPages = 1;

  // Filters
  let filterStartDate = '';
  let filterEndDate = '';
  let filterEndpoint = '';
  let filterStatusCode = '';
  let filterClientIp = '';
  let filterUserId = '';
  let filterAiModelVersion = '';

  // Modal State
  let selectedLog = null;
  let showModal = false;

  onMount(() => {
      fetchLogs();
  });

  async function fetchLogs() {
      isLoading = true;
      try {
          let url = `/admin/logs?page=${page}&pageSize=${pageSize}`;
          if (filterStartDate) {
              try {
                  const utcStart = new Date(filterStartDate).toISOString();
                  url += `&startDate=${encodeURIComponent(utcStart)}`;
              } catch (e) {
                  url += `&startDate=${encodeURIComponent(filterStartDate)}`;
              }
          }
          if (filterEndDate) {
              try {
                  const utcEnd = new Date(filterEndDate).toISOString();
                  url += `&endDate=${encodeURIComponent(utcEnd)}`;
              } catch (e) {
                  url += `&endDate=${encodeURIComponent(filterEndDate)}`;
              }
          }
          if (filterEndpoint) url += `&endpoint=${encodeURIComponent(filterEndpoint)}`;
          if (filterStatusCode) url += `&statusCode=${encodeURIComponent(filterStatusCode)}`;
          if (filterClientIp) url += `&clientIp=${encodeURIComponent(filterClientIp)}`;
          if (filterUserId) url += `&userId=${encodeURIComponent(filterUserId)}`;
          if (filterAiModelVersion) url += `&aiModelVersion=${encodeURIComponent(filterAiModelVersion)}`;

          const res = await authFetch(getApiUrl(url));
          if (!res.ok) throw new Error("Failed to fetch logs");
          
          const result = await res.json();
          logs = result.data || [];
          totalPages = result.totalPages || 1;
      } catch (e) {
          error = e.message;
      } finally {
          isLoading = false;
      }
  }

  function handleFilter() {
      page = 1;
      fetchLogs();
  }

  function clearFilters() {
      filterStartDate = '';
      filterEndDate = '';
      filterEndpoint = '';
      filterStatusCode = '';
      filterClientIp = '';
      filterUserId = '';
      filterAiModelVersion = '';
      page = 1;
      fetchLogs();
  }

  function viewDetails(log) {
      selectedLog = log;
      showModal = true;
  }

  function closeModal() {
      showModal = false;
      selectedLog = null;
  }

  function nextPage() { if(page < totalPages) { page++; fetchLogs(); } }
  function prevPage() { if(page > 1) { page--; fetchLogs(); } }

  function getStatusColor(code) {
      if (code >= 200 && code < 300) return 'status-success';
      if (code >= 400 && code < 500) return 'status-warning';
      return 'status-error';
  }
</script>

<div class="admin-container">
  <h2>{$t('admin.logs.title')}</h2>
  <p class="subtitle">{$t('admin.logs.subtitle')}</p>

  <!-- Filters Section -->
  <div class="filters-card">
    <h3>{$t('admin.logs.filters.title')}</h3>
    <div class="filters-grid">
      <div class="filter-field">
        <label for="start-date">{$t('admin.logs.filters.start_date')}</label>
        <input type="datetime-local" id="start-date" bind:value={filterStartDate} />
      </div>
      <div class="filter-field">
        <label for="end-date">{$t('admin.logs.filters.end_date')}</label>
        <input type="datetime-local" id="end-date" bind:value={filterEndDate} />
      </div>
      <div class="filter-field">
        <label for="endpoint">{$t('admin.logs.filters.endpoint')}</label>
        <input type="text" id="endpoint" placeholder="/promotions/recommend" bind:value={filterEndpoint} />
      </div>
      <div class="filter-field">
        <label for="status-code">{$t('admin.logs.filters.status')}</label>
        <input type="number" id="status-code" placeholder="200" bind:value={filterStatusCode} />
      </div>
      <div class="filter-field">
        <label for="client-ip">{$t('admin.logs.filters.ip')}</label>
        <input type="text" id="client-ip" placeholder="127.0.0.1" bind:value={filterClientIp} />
      </div>
      <div class="filter-field">
        <label for="user-id">{$t('admin.logs.filters.user_id')}</label>
        <input type="text" id="user-id" placeholder="Anonymous" bind:value={filterUserId} />
      </div>
      <div class="filter-field">
        <label for="ai-version">{$t('admin.logs.filters.ai_version')}</label>
        <input type="text" id="ai-version" placeholder="gemini-3.5-flash" bind:value={filterAiModelVersion} />
      </div>
    </div>
    <div class="filters-actions">
      <button class="btn btn-primary" on:click={handleFilter}>{$t('admin.logs.filters.btn_filter')}</button>
      <button class="btn btn-secondary" on:click={clearFilters}>{$t('admin.logs.filters.btn_clear')}</button>
    </div>
  </div>

  {#if isLoading}
      <p class="loading">{$t('admin.logs.loading')}</p>
  {:else if error}
      <p class="error">{error}</p>
  {:else if logs.length === 0}
      <p class="no-data">{$t('admin.logs.no_data')}</p>
  {:else}
      <div class="table-responsive">
          <table>
              <thead>
                  <tr>
                      <th>{$t('admin.logs.time')}</th>
                      <th>{$t('admin.logs.endpoint')}</th>
                      <th>{$t('admin.logs.status')}</th>
                      <th>{$t('admin.logs.ip_address')}</th>
                      <th>{$t('admin.logs.user_id')}</th>
                      <th>{$t('admin.logs.filters.ai_version')}</th>
                      <th>{$t('admin.logs.message')}</th>
                      <th>{$t('admin.logs.action')}</th>
                  </tr>
              </thead>
              <tbody>
                  {#each logs as log}
                      <tr>
                          <td>{new Date(log.timestamp).toLocaleString()}</td>
                          <td class="endpoint">{log.endpoint}</td>
                          <td>
                              <span class="badge {getStatusColor(log.statusCode)}">{log.statusCode}</span>
                          </td>
                          <td class="code-font">{log.clientIp}</td>
                          <td class="code-font">{log.userId || 'Guest'}</td>
                          <td class="code-font">{log.aiModelVersion || '-'}</td>
                          <td class="truncate" title={log.message}>{log.message}</td>
                          <td>
                              <button class="view-btn" on:click={() => viewDetails(log)}>{$t('admin.logs.details')}</button>
                          </td>
                      </tr>
                  {/each}
              </tbody>
          </table>
      </div>

      <div class="pagination">
          <button disabled={page <= 1} on:click={prevPage}>{$t('admin.logs.prev')}</button>
          <span>{$t('admin.logs.page_of').replace('{page}', page.toString()).replace('{totalPages}', totalPages.toString())}</span>
          <button disabled={page >= totalPages} on:click={nextPage}>{$t('admin.logs.next')}</button>
      </div>
  {/if}
</div>

<!-- Details Modal -->
{#if showModal && selectedLog}
  <div class="modal-backdrop" on:click={closeModal}>
      <div class="modal-content" on:click|stopPropagation>
          <div class="modal-header">
              <h3>{$t('admin.logs.details_title')}</h3>
              <button class="close-btn" on:click={closeModal}>&times;</button>
          </div>
          <div class="modal-body">
              <div class="info-grid">
                  <div><strong>{$t('admin.logs.status_label')}</strong> <span class="badge {getStatusColor(selectedLog.statusCode)}">{selectedLog.statusCode}</span></div>
                  <div><strong>{$t('admin.logs.time_label')}</strong> {new Date(selectedLog.timestamp).toLocaleString()}</div>
                  <div><strong>{$t('admin.logs.endpoint_label')}</strong> {selectedLog.endpoint}</div>
                  <div><strong>{$t('admin.logs.ip_label')}</strong> {selectedLog.clientIp}</div>
                  <div><strong>{$t('admin.logs.user_id_label')}</strong> {selectedLog.userId || 'Guest'}</div>
                  <div><strong>{$t('admin.logs.filters.ai_version')}:</strong> {selectedLog.aiModelVersion || '-'}</div>
              </div>
              
              <div class="section">
                  <strong>{$t('admin.logs.message_label')}</strong>
                  <p class="msg-box">{selectedLog.message}</p>
              </div>

              <div class="section">
                  <strong>{$t('admin.logs.payload_label')}</strong>
                  <pre class="payload-box">{selectedLog.payload || $t('admin.logs.no_payload')}</pre>
              </div>
          </div>
      </div>
  </div>
{/if}

<style>
  .admin-container { max-width: 1200px; margin: 2rem auto; padding: 0 1rem; }
  h2 { margin-bottom: 0.5rem; color: #333; }
  .subtitle { color: #666; margin-bottom: 2rem; }

  /* Filters styles */
  .filters-card { background: #fff; padding: 1.5rem; border-radius: 8px; margin-bottom: 2rem; box-shadow: 0 2px 4px rgba(0,0,0,0.05); border: 1px solid #eee; }
  .filters-card h3 { margin-top: 0; margin-bottom: 1rem; color: #444; font-size: 1.1rem; }
  .filters-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; }
  .filter-field { display: flex; flex-direction: column; gap: 0.25rem; }
  .filter-field label { font-size: 0.8rem; font-weight: 600; color: #666; }
  .filter-field input { padding: 0.5rem; border: 1px solid #ccc; border-radius: 4px; font-size: 0.9rem; }
  .filters-actions { display: flex; gap: 1rem; }
  .btn { padding: 0.5rem 1.5rem; border-radius: 4px; font-weight: 600; cursor: pointer; border: none; font-size: 0.9rem; transition: background 0.2s; }
  .btn-primary { background: #007bff; color: #fff; }
  .btn-primary:hover { background: #0056b3; }
  .btn-secondary { background: #6c757d; color: #fff; }
  .btn-secondary:hover { background: #5a6268; }

  .table-responsive { overflow-x: auto; background: #fff; border-radius: 8px; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }
  table { width: 100%; border-collapse: collapse; text-align: left; font-size: 0.9rem; }
  th, td { padding: 12px; border-bottom: 1px solid #eee; }
  th { background: #f8f9fa; font-weight: 600; color: #555; }
  
  .endpoint { font-family: monospace; color: #0056b3; }
  .code-font { font-family: monospace; color: #555; font-size: 0.85rem; }
  .truncate { max-width: 200px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  
  .badge { padding: 3px 8px; border-radius: 12px; font-weight: bold; font-size: 0.8rem; }
  .status-success { background: #d4edda; color: #155724; }
  .status-warning { background: #fff3cd; color: #856404; }
  .status-error { background: #f8d7da; color: #721c24; }

  .view-btn { background: #e9ecef; border: none; padding: 4px 10px; border-radius: 4px; cursor: pointer; }
  .view-btn:hover { background: #dde2e6; }

  .pagination { display: flex; justify-content: center; gap: 1rem; margin-top: 1.5rem; align-items: center; }
  .pagination button { padding: 0.5rem 1rem; border: 1px solid #ccc; background: #fff; border-radius: 4px; cursor: pointer; }
  .pagination button:disabled { background: #f8f9fa; color: #ccc; cursor: not-allowed; }

  /* Modal Styles */
  .modal-backdrop { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0,0,0,0.5); display: flex; justify-content: center; align-items: center; z-index: 1000; padding: 1rem; box-sizing: border-box; }
  .modal-content { background: #fff; width: 100%; max-width: 700px; border-radius: 8px; max-height: 90vh; display: flex; flex-direction: column; }
  .modal-header { display: flex; justify-content: space-between; align-items: center; padding: 1rem 1.5rem; border-bottom: 1px solid #eee; background: #f8f9fa; }
  .modal-header h3 { margin: 0; }
  .close-btn { background: none; border: none; font-size: 1.5rem; cursor: pointer; }
  
  .modal-body { padding: 1.5rem; overflow-y: auto; }
  .info-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin-bottom: 1.5rem; background: #f8f9fa; padding: 1rem; border-radius: 6px; }
  .section { margin-bottom: 1.5rem; }
  .msg-box { background: #fff3cd; color: #856404; padding: 1rem; border-radius: 4px; border-left: 4px solid #ffeeba; margin-top: 0.5rem; }
  .payload-box { background: #272822; color: #f8f8f2; padding: 1rem; border-radius: 4px; overflow-x: auto; font-family: monospace; font-size: 0.9rem; margin-top: 0.5rem; white-space: pre-wrap; word-break: break-all; }
</style>